import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PortableText } from '@portabletext/react';
import {
  getIslandAreaMapPoints,
  getIslandCapabilities,
  getIslandConfig,
  getIslandFromHostname,
  slugifyGeographyName,
} from 'alakai-aloha-shared';
import { AreaDetailView } from 'alakai-aloha-shared/public-ui';
import { fetchAreaBySlug, fetchAreasByRegion } from '../../lib/sanityHelpers';
import { buildRuntimeCanonicalUrl, getRuntimePublicBaseUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { trackEvent } from '../../utils/analytics';
import { canonicalizeAreaName } from '../../utils/areaNameFormatting';
import { normalizeActivityDocument } from '../../lib/activityTaxonomy';

const portableTextComponents = {
  types: {
    image: ({ value }) => (
      <img
        src={value.asset?.url}
        alt={value.alt || 'Area image'}
        className="my-4 rounded"
      />
    ),
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={value.href}
        className="underline hover"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl font-extrabold tracking-tight mt-12 mb-6">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl font-bold mt-10 mb-4 border-b pb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-semibold mt-6 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg font-medium italic mt-4 mb-1">{children}</h4>,
    normal: ({ children }) => <p className="mb-4">{children}</p>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
};

function mapExperience(experience) {
  const activity = normalizeActivityDocument(experience);
  return {
    id: activity._id,
    name: activity.name,
    slug: activity.slug,
    area: activity.area,
    category: activity.displayCategory || activity.category,
    cost: activity.cost,
    shortDescription: activity.shortDescription,
    imageUrl: activity.images?.[0]?.asset?.url,
    imageAlt: activity.images?.[0]?.alt || activity.name,
  };
}

const AreaDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const siteContext = React.useMemo(() => getRuntimeSiteContext(), []);
  const runtimeIsland = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return getIslandFromHostname(window.location.hostname) || siteContext.island;
    }

    return siteContext.island;
  }, [siteContext.island]);
  const islandConfig = React.useMemo(() => getIslandConfig(runtimeIsland), [runtimeIsland]);
  const capabilities = React.useMemo(() => getIslandCapabilities(runtimeIsland), [runtimeIsland]);
  const areaMapPoints = React.useMemo(() => getIslandAreaMapPoints(runtimeIsland), [runtimeIsland]);
  const [areaData, setAreaData] = useState(null);
  const [neighboringAreas, setNeighboringAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const redirectedSlug = slug ? slugifyGeographyName(canonicalizeAreaName(slug)) : null;
    if (slug && redirectedSlug && redirectedSlug !== slug) {
      navigate(`/areas/${redirectedSlug}`, { replace: true });
      return undefined;
    }

    const fetchAreaData = async () => {
      setLoading(true);

      try {
        const sanityData = await fetchAreaBySlug(slug);
        if (!sanityData) {
          setError(`Area "${slug}" not found in Sanity CMS.`);
          return;
        }

        setAreaData(sanityData);
        const siblingAreas = await fetchAreasByRegion(sanityData.region.slug.current);
        setNeighboringAreas((siblingAreas || []).filter((item) => item._id !== sanityData._id));
      } catch (fetchError) {
        console.error('Error fetching area data:', fetchError);
        setError(`Failed to load area data: ${fetchError.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchAreaData();
    }
  }, [navigate, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading area details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-2xl mx-auto text-center p-8">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Sanity Data Required</h1>
          <p className="text-lg mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (!areaData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Area not found</div>
      </div>
    );
  }

  const siteUrl = getRuntimePublicBaseUrl();
  const pageUrl = buildRuntimeCanonicalUrl(`/areas/${slug}`);
  const pageTitle = areaData.seo?.title || `${areaData.title} | ${areaData.region?.title} | ${islandConfig.shortTitle} Travel Guide`;
  const pageDescription =
    areaData.seo?.description ||
    areaData.oneLiner ||
    `Discover ${areaData.title} in ${areaData.region?.title} region of ${islandConfig.title}.`;
  const pageImage = areaData.heroImage?.asset?.url || null;
  const placeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: areaData.title,
    description: pageDescription,
    url: pageUrl,
    ...(pageImage ? { image: pageImage } : {}),
    ...(areaData.region?.title
      ? {
          containedInPlace: {
            '@type': 'Place',
            name: areaData.region.title,
          },
        }
      : {}),
  };
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Regions',
      item: `${siteUrl}/regions`,
    },
  ];
  if (areaData.region?.title && areaData.region?.slug?.current) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: areaData.region.title,
      item: `${siteUrl}/regions/${areaData.region.slug.current}`,
    });
  }
  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbItems.length + 1,
    name: areaData.title,
    item: pageUrl,
  });

  return (
    <div className="relative z-20">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="Alakai Aloha" />
        {pageImage && <meta property="og:image" content={pageImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}

        <script type="application/ld+json">{JSON.stringify(placeJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: breadcrumbItems })}</script>
      </Helmet>

      <AreaDetailView
        islandKey={runtimeIsland}
        area={{
          title: areaData.title,
          slug: areaData.slug?.current || areaData.slug,
          oneLiner: areaData.oneLiner,
          fit: areaData.fit || [],
          tradeoffs: areaData.tradeoffs || [],
          walkability: areaData.walkability,
          beachProfile: areaData.beachProfile,
          diningDensity: areaData.diningDensity,
          logisticsNotes: areaData.logisticsNotes,
          heroImageUrl: areaData.heroImage?.asset?.url,
          heroImageAlt: areaData.heroImage?.alt,
          articleContent: areaData.reviewArticle
            ? <PortableText value={areaData.reviewArticle} components={portableTextComponents} />
            : null,
          parentRegion: areaData.region
            ? {
                title: areaData.region.title,
                slug: areaData.region.slug?.current || areaData.region.slug,
              }
            : undefined,
          neighboringAreas: neighboringAreas.map((area) => ({
            id: area._id,
            title: area.title,
            slug: area.slug?.current || area.slug,
            oneLiner: area.oneLiner,
            heroImageUrl: area.heroImage?.asset?.url,
            heroImageAlt: area.heroImage?.alt,
          })),
          signatureExperiences: (areaData.signatureExperiences || []).map(mapExperience),
        }}
        areaMapPoints={areaMapPoints}
        mapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
        showCatalogLinks={capabilities.profileCatalogs}
        showPlannerPromo={capabilities.tripPlanner || capabilities.stayQuiz}
        onMapAreaSelect={(areaSlug) => navigate(`/areas/${areaSlug}`)}
        onQuizClick={() => {
          trackEvent('quiz_cta_click', { source: 'area_profile_promo_hero', cta: 'primary' });
        }}
        onInterviewClick={() => {
          trackEvent('interview_cta_click', { source: 'area_profile_promo_hero', cta: 'secondary' });
        }}
      />
    </div>
  );
};

export default AreaDetail;
